import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LogOut, TrendingDown, Sparkles, FolderOpen, FileText, UploadCloud, ArrowUpRight, CalendarDays, XCircle, Download, Calculator, MessageCircle, ChevronDown, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Progress } from "@/components/ui/progress";

const STATUS_STYLE = {
  aperto: "bg-[#FFFFFF] text-[#2d6fc4]",
  analisi: "bg-[#F1F5F9] text-[#2d6fc4]",
  trattativa: "bg-[#F1F5F9] text-[#2d6fc4]",
  chiuso: "bg-[#F1F5F9] text-[#2d6fc4]",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [cases, setCases] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get("/cases").then(r => setCases(r.data)).catch(()=>{}),
      api.get("/documents").then(r => setDocs(r.data)).catch(()=>{}),
    ]).finally(()=>setLoading(false));
  }, []);

  const totalInitial = cases.reduce((s, c) => s + c.debt_initial, 0);
  const totalCurrent = cases.reduce((s, c) => s + c.debt_current, 0);
  const savingPct = totalInitial > 0 ? Math.round((1 - totalCurrent / totalInitial) * 100) : 0;

  return (
    <div className="pt-28 pb-16 bg-[#F1F5F9] min-h-screen" data-testid="dashboard-page">
      <div className="container-editorial">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-8 border-b border-[#E5E7EB]">
          <div>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Area riservata</p>
            <h1 className="font-serif text-5xl md:text-6xl mt-3 leading-[0.9]">
              Ciao, {user?.name?.split(" ")[0] || "cliente"}.
            </h1>
            <p className="mt-2 text-[#333333] text-sm">Il tuo dossier è aggiornato in tempo reale.</p>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sm border border-[#E5E7EB] px-4 py-2 rounded-lg hover:border-[#14417B]" style={{transition:"border-color 250ms ease"}}>
            <LogOut size={14}/> Esci
          </button>
        </div>

        {/* Section nav */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a4a8a] text-white text-xs tracking-wide">
            <Sparkles size={12}/> Pratiche
          </span>
          <Link to="/documenti" data-testid="dashboard-docs-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5E7EB] hover:border-[#14417B] text-xs text-[#2d6fc4]" style={{transition:"border-color 200ms ease"}}>
            <FolderOpen size={12}/> Documenti
          </Link>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-12 gap-4 mt-10">
          <KpiCard label="Debito iniziale" value={fmt(totalInitial)} sub={`${cases.length} pratiche`} icon={TrendingDown}/>
          <KpiCard label="Debito attuale" value={fmt(totalCurrent)} sub="in trattativa" accent icon={Sparkles}/>
          <KpiCard label="Riduzione totale" value={`-${savingPct}%`} sub={`${fmt(totalInitial-totalCurrent)} risparmiati`} big/>
        </div>

        {/* Admin: prenotazioni */}
        {user?.role === "admin" && <AdminAppointments />}
        {user?.role === "admin" && <AdminLeads />}
        {user?.role === "admin" && <AdminContractLeads />}
        {user?.role === "admin" && <AdminWhatsApp />}
        {user?.role === "admin" && <AdminNewsletter />}

        {/* Documents card — prominent */}
        <div className="mt-10 grid grid-cols-12 gap-4" data-testid="dashboard-documents-card">
          <div className="col-span-12 md:col-span-8 bg-[#1a4a8a] text-white rounded-lg p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">— I tuoi documenti</p>
              <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
                Allega qui i tuoi <em className="italic text-white">PDF</em>.
              </h2>
              <p className="mt-3 text-sm text-white max-w-md">
                Carica estratti conto, decreti, contratti, cartelle esattoriali. Restano nel tuo dossier privato — accessibile solo a te e al tuo consulente.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3">
              <Link to="/documenti" data-testid="dashboard-docs-upload" className="btn-cta inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm">
                <UploadCloud size={16}/> Carica PDF
              </Link>
              <Link to="/documenti" data-testid="dashboard-docs-open" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/70 px-5 py-3 rounded-lg text-sm" style={{transition:"border-color 200ms ease"}}>
                Vedi archivio <ArrowUpRight size={14}/>
              </Link>
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 bg-white border border-[#E5E7EB] rounded-lg p-6">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Ultimi caricamenti</p>
              <FolderOpen size={14} className="text-[#333333]"/>
            </div>
            <p className="font-serif text-4xl text-[#2d6fc4] mt-3">{docs.length}</p>
            <p className="text-xs text-[#333333] mt-1">documenti nell'archivio</p>
            <div className="mt-4 space-y-2">
              {docs.slice(0, 3).map((d, i) => (
                <Link key={d.id} to="/documenti" data-testid={`dashboard-doc-${i}`} className="flex items-center gap-2 text-xs text-[#2d6fc4] hover:text-[#2d6fc4] py-1 truncate" style={{transition:"color 200ms ease"}}>
                  <FileText size={12} strokeWidth={1.5}/>
                  <span className="truncate">{d.original_filename}</span>
                </Link>
              ))}
              {docs.length === 0 && !loading && (
                <p className="text-xs text-[#333333] italic">Nessun documento ancora.</p>
              )}
            </div>
          </div>
        </div>

        {/* Cases table */}
        <div className="mt-10 border border-[#E5E7EB] bg-[#FFFFFF] rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
            <h2 className="font-serif text-2xl">Le tue pratiche</h2>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{cases.length} totali</p>
          </div>
          {loading && <div className="p-10 text-center text-[#333333]">Caricamento…</div>}
          {!loading && cases.length === 0 && <div className="p-10 text-center text-[#333333]">Nessuna pratica al momento.</div>}
          {!loading && cases.length > 0 && (
            <div className="divide-y divide-[#E5E7EB]">
              {cases.map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i*0.05 }}
                  className="p-6 grid grid-cols-12 gap-4 items-center"
                  data-testid={`case-row-${i}`}
                >
                  <div className="col-span-12 md:col-span-6">
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 rounded ${STATUS_STYLE[c.status] || ""}`}>{c.status}</span>
                    </div>
                    <p className="mt-2 font-medium text-[#2d6fc4]">{c.title}</p>
                    <p className="text-sm text-[#333333] mt-1 max-w-xl">{c.notes}</p>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <div className="flex items-center justify-between text-xs text-[#333333] mb-1">
                      <span>Avanzamento</span><span>{c.progress}%</span>
                    </div>
                    <Progress value={c.progress} className="h-1.5 bg-[#E5E7EB]"/>
                    <div className="mt-3 text-xs text-[#333333]">
                      {fmt(c.debt_initial)} → <span className="font-medium text-[#2d6fc4]">{fmt(c.debt_current)}</span>
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-2 text-right">
                    <p className="font-serif text-3xl text-[#2d6fc4]">-{c.debt_initial > 0 ? Math.round((1 - c.debt_current/c.debt_initial)*100) : 0}%</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 text-sm text-[#333333]">
          Serve un aggiornamento urgente? <a href="https://wa.me/393927715932" className="text-[#2d6fc4] link-underline pb-0.5">Scrivici su WhatsApp</a>.
        </div>
      </div>
    </div>
  );
}

const PROBLEM_LABELS = { personale: "Debiti personali", aziendale: "Debiti aziendali", mutuo: "Mutuo / casa", fiscale: "Cartelle esattoriali" };
const REM_LABEL = { whatsapp: "promemoria WhatsApp inviato", email: "promemoria email inviato" };

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const AdminLeads = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/calculator-leads").then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const exportLeads = async () => {
    try {
      const r = await api.get("/calculator-leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lead-calcolatore-sdebitiamoci.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nell'esportazione");
    }
  };

  const fmtDT = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="mt-10 border border-[#E5E7EB] bg-white rounded-lg overflow-hidden" data-testid="admin-leads-panel">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
        <h2 className="font-serif text-2xl flex items-center gap-2"><Calculator size={20} strokeWidth={1.5}/> Lead dal calcolatore</h2>
        <div className="flex items-center gap-4">
          <button onClick={exportLeads} data-testid="export-leads-btn"
            className="inline-flex items-center gap-1.5 text-xs border border-[#E5E7EB] px-3 py-2 rounded-lg text-[#2d6fc4] hover:border-[#14417B]"
            style={{ transition: "border-color 200ms ease" }}>
            <Download size={13}/> Esporta Excel
          </button>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{items.length} lead</p>
        </div>
      </div>
      {loading && <div className="p-8 text-center text-[#333333]">Caricamento…</div>}
      {!loading && items.length === 0 && <div className="p-8 text-center text-[#333333]" data-testid="admin-no-leads">Nessun lead dal calcolatore al momento.</div>}
      {!loading && items.length > 0 && (
        <div className="divide-y divide-[#E5E7EB]">
          {items.map((l, i) => (
            <div key={l.id} className="p-5 grid grid-cols-12 gap-3 items-center" data-testid={`lead-row-${i}`}>
              <div className="col-span-6 md:col-span-3">
                <p className="font-medium text-[#2d6fc4]">{l.name}</p>
                <p className="text-xs text-[#333333] truncate">{l.email}</p>
              </div>
              <div className="col-span-6 md:col-span-3">
                <p className="font-serif text-xl text-[#2d6fc4]">{eur(l.amount)}</p>
                <p className="text-xs text-[#333333]">{PROBLEM_LABELS[l.debt_type] || l.debt_type}</p>
              </div>
              <div className="col-span-6 md:col-span-4">
                <p className="text-sm text-[#2d6fc4] font-medium">Risparmio mostrato: {eur(l.save_min)} – {eur(l.save_max)}</p>
              </div>
              <div className="col-span-6 md:col-span-2 text-right">
                <p className="text-xs text-[#333333]">{fmtDT(l.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminContractLeads = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/contract-leads").then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get("/contract-leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lead-facsimile-sdebitiamoci.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nell'esportazione");
    }
  };

  const fmtDT = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="mt-10 border border-[#E5E7EB] bg-white rounded-lg overflow-hidden" data-testid="admin-contract-leads-panel">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
        <h2 className="font-serif text-2xl flex items-center gap-2"><FileText size={20} strokeWidth={1.5}/> Lead fac-simile contratto</h2>
        <div className="flex items-center gap-4">
          <button onClick={exportCsv} data-testid="export-contract-leads-btn"
            className="inline-flex items-center gap-1.5 text-xs border border-[#E5E7EB] px-3 py-2 rounded-lg text-[#2d6fc4] hover:border-[#14417B]"
            style={{ transition: "border-color 200ms ease" }}>
            <Download size={13}/> Esporta Excel
          </button>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{items.length} lead</p>
        </div>
      </div>
      {loading && <div className="p-8 text-center text-[#333333]">Caricamento…</div>}
      {!loading && items.length === 0 && <div className="p-8 text-center text-[#333333]" data-testid="admin-no-contract-leads">Nessuna richiesta di fac-simile al momento.</div>}
      {!loading && items.length > 0 && (
        <div className="divide-y divide-[#E5E7EB]">
          {items.map((l, i) => (
            <div key={l.id} className="p-5 grid grid-cols-12 gap-3 items-center" data-testid={`contract-lead-row-${i}`}>
              <div className="col-span-6 md:col-span-4">
                <p className="font-medium text-[#2d6fc4]">{l.name}</p>
              </div>
              <div className="col-span-6 md:col-span-5">
                <p className="text-sm text-[#2d6fc4] truncate">{l.email}</p>
              </div>
              <div className="col-span-12 md:col-span-3 text-left md:text-right">
                <p className="text-xs text-[#333333]">{fmtDT(l.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminAppointments = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/appointments").then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm("Annullare questa prenotazione? Lo slot tornerà disponibile.")) return;
    try {
      await api.post(`/appointments/${id}/cancel`);
      toast.success("Prenotazione annullata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'annullamento");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/appointments/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "prenotazioni-sdebitiamoci.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nell'esportazione");
    }
  };

  const fmtD = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mt-10 border border-[#E5E7EB] bg-white rounded-lg overflow-hidden" data-testid="admin-appointments-panel">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
        <h2 className="font-serif text-2xl flex items-center gap-2"><CalendarDays size={20} strokeWidth={1.5}/> Chiamate prenotate</h2>
        <div className="flex items-center gap-4">
          <button onClick={exportCsv} data-testid="export-appointments-btn"
            className="inline-flex items-center gap-1.5 text-xs border border-[#E5E7EB] px-3 py-2 rounded-lg text-[#2d6fc4] hover:border-[#14417B]"
            style={{ transition: "border-color 200ms ease" }}>
            <Download size={13}/> Esporta Excel
          </button>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{items.filter(a => a.status === "confermato").length} attive</p>
        </div>
      </div>
      {loading && <div className="p-8 text-center text-[#333333]">Caricamento…</div>}
      {!loading && items.length === 0 && <div className="p-8 text-center text-[#333333]" data-testid="admin-no-appointments">Nessuna prenotazione al momento.</div>}
      {!loading && items.length > 0 && (
        <div className="divide-y divide-[#E5E7EB]">
          {items.map((a, i) => {
            const cancelled = a.status === "annullato";
            const past = a.date < today;
            return (
              <div key={a.id} className={`p-5 grid grid-cols-12 gap-3 items-center ${cancelled || past ? "opacity-50" : ""}`} data-testid={`appointment-row-${i}`}>
                <div className="col-span-6 md:col-span-2">
                  <p className="font-serif text-xl text-[#2d6fc4]">{fmtD(a.date)}</p>
                  <p className="font-mono text-sm text-[#2d6fc4]">{a.time}</p>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <p className="font-medium text-[#2d6fc4]">{a.name}</p>
                  <p className="text-xs text-[#333333]">{PROBLEM_LABELS[a.problem_type] || a.problem_type}</p>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <p className="text-sm text-[#2d6fc4]">{a.phone}</p>
                  <p className="text-xs text-[#333333] truncate">{a.email}</p>
                </div>
                <div className="col-span-3 md:col-span-2">
                  <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 rounded ${cancelled ? "bg-[#F1F5F9] text-[#333333]" : "bg-[#F1F5F9] text-[#2d6fc4]"}`}>
                    {a.status}
                  </span>
                  {a.reminder_sent && <p className="text-[10px] text-[#333333] mt-1">{REM_LABEL[a.reminder_channel] || "promemoria inviato"}</p>}
                </div>
                <div className="col-span-3 md:col-span-2 text-right">
                  {!cancelled && !past && (
                    <button onClick={() => cancel(a.id)} data-testid={`cancel-appointment-btn-${i}`}
                      className="inline-flex items-center gap-1.5 text-xs border border-[#E5E7EB] px-3 py-2 rounded-lg text-[#2d6fc4] hover:border-[#B91C1C] hover:text-[#B91C1C]"
                      style={{ transition: "border-color 200ms ease, color 200ms ease" }}>
                      <XCircle size={13}/> Annulla
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminWhatsApp = () => {
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [msgs, setMsgs] = useState({});
  useEffect(() => {
    api.get("/whatsapp/conversations").then(r => setConvos(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = async (waFrom) => {
    if (openId === waFrom) { setOpenId(null); return; }
    setOpenId(waFrom);
    if (!msgs[waFrom]) {
      try {
        const r = await api.get("/whatsapp/conversations/messages", { params: { wa_from: waFrom } });
        setMsgs(m => ({ ...m, [waFrom]: r.data }));
      } catch {
        toast.error("Errore nel caricamento dei messaggi");
      }
    }
  };

  const fmtDT = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const num = (w) => w.replace("whatsapp:", "");

  return (
    <div className="mt-10 border border-[#E5E7EB] bg-white rounded-lg overflow-hidden" data-testid="admin-whatsapp-panel">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
        <h2 className="font-serif text-2xl flex items-center gap-2"><MessageCircle size={20} strokeWidth={1.5}/> Conversazioni WhatsApp</h2>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{convos.length} contatti</p>
      </div>
      {loading && <div className="p-8 text-center text-[#333333]">Caricamento…</div>}
      {!loading && convos.length === 0 && <div className="p-8 text-center text-[#333333]" data-testid="admin-no-whatsapp">Nessuna conversazione WhatsApp al momento.</div>}
      {!loading && convos.length > 0 && (
        <div className="divide-y divide-[#E5E7EB]">
          {convos.map((c, i) => (
            <div key={c.wa_from}>
              <button onClick={() => toggle(c.wa_from)} data-testid={`wa-convo-row-${i}`}
                className="w-full text-left p-5 grid grid-cols-12 gap-3 items-center hover:bg-[#F8FAFC]"
                style={{ transition: "background-color 150ms ease" }}>
                <div className="col-span-6 md:col-span-3">
                  <p className="font-medium text-[#2d6fc4]">{num(c.wa_from)}</p>
                  <p className="text-xs text-[#333333]">{c.count} messaggi</p>
                </div>
                <div className="col-span-6 md:col-span-6">
                  <p className="text-sm text-[#333333] truncate">
                    <span className="font-medium text-[#2d6fc4]">{c.last_role === "assistant" ? "Emmi: " : "Cliente: "}</span>
                    {c.last_message}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-3 flex items-center justify-between md:justify-end gap-3">
                  <p className="text-xs text-[#333333]">{fmtDT(c.last_at)}</p>
                  <ChevronDown size={14} className={`text-[#333333] ${openId === c.wa_from ? "rotate-180" : ""}`} style={{ transition: "transform 200ms ease" }}/>
                </div>
              </button>
              {openId === c.wa_from && (
                <div className="px-5 pb-5 bg-[#F8FAFC] max-h-96 overflow-y-auto space-y-3 pt-4" data-testid={`wa-convo-messages-${i}`}>
                  <div className="flex justify-end">
                    <a href={`https://wa.me/${num(c.wa_from).replace(/[^\d]/g, "")}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`wa-reply-btn-${i}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs text-white bg-[#25D366] hover:bg-[#1ebe5b]"
                      style={{ transition: "background-color 200ms ease" }}>
                      <MessageCircle size={13}/> Rispondi su WhatsApp
                    </a>
                  </div>
                  {!msgs[c.wa_from] && <p className="text-xs text-[#333333]">Caricamento messaggi…</p>}
                  {(msgs[c.wa_from] || []).map((m, j) => (
                    <div key={`${m.created_at}-${j}`} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${m.role === "user" ? "bg-white border border-[#E5E7EB] text-[#333333]" : "bg-[#1a4a8a] text-white"}`}>
                        <p className="whitespace-pre-wrap">{m.content}</p>
                        {m.media_url && <p className="mt-1 text-xs opacity-80">📎 Allegato PDF inviato</p>}
                        <p className={`mt-1 text-[10px] ${m.role === "user" ? "text-[#94A3B8]" : "text-white/60"}`}>{fmtDT(m.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminNewsletter = () => {
  const [items, setItems] = useState([]);
  const [posts, setPosts] = useState([]);
  const [announced, setAnnounced] = useState([]);
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get("/newsletter/subscribers").then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/blog").then(r => setPosts(r.data)).catch(() => {});
    api.get("/newsletter/announcements").then(r => setAnnounced(r.data.map(a => a.slug))).catch(() => {});
  }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get("/newsletter/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "iscritti-newsletter-sdebitiamoci.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nell'esportazione");
    }
  };

  const announce = async () => {
    if (!slug) { toast.error("Seleziona un articolo da annunciare"); return; }
    setSending(true);
    try {
      const r = await api.post(`/newsletter/announce/${slug}`);
      toast.success(`Annuncio inviato a ${r.data.sent} iscritti`);
      setAnnounced(a => [...a, slug]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio dell'annuncio");
    } finally {
      setSending(false);
    }
  };

  const fmtDT = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="mt-10 border border-[#E5E7EB] bg-white rounded-lg overflow-hidden" data-testid="admin-newsletter-panel">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-2xl flex items-center gap-2"><Mail size={20} strokeWidth={1.5}/> Newsletter blog</h2>
        <div className="flex items-center gap-4">
          <button onClick={exportCsv} data-testid="export-newsletter-btn"
            className="inline-flex items-center gap-1.5 text-xs border border-[#E5E7EB] px-3 py-2 rounded-lg text-[#2d6fc4] hover:border-[#14417B]"
            style={{ transition: "border-color 200ms ease" }}>
            <Download size={13}/> Esporta Excel
          </button>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{items.length} iscritti</p>
        </div>
      </div>
      <div className="px-6 py-4 border-b border-[#E5E7EB] bg-[#F8FAFC] flex flex-col md:flex-row gap-3 md:items-center">
        <p className="text-sm text-[#333333] whitespace-nowrap">Annuncia un articolo agli iscritti:</p>
        <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="newsletter-announce-select"
          className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-sm bg-white text-[#333333] focus:outline-none focus:border-[#2d6fc4]">
          <option value="">Seleziona un articolo…</option>
          {posts.map(p => <option key={p.slug} value={p.slug}>{announced.includes(p.slug) ? "✓ già inviato — " : ""}{p.title}</option>)}
        </select>
        <button onClick={announce} disabled={sending} data-testid="newsletter-announce-btn"
          className="btn-cta px-5 py-2.5 rounded-lg text-xs tracking-wide disabled:opacity-60 whitespace-nowrap">
          {sending ? "Invio in corso…" : "Invia annuncio"}
        </button>
      </div>
      {loading && <div className="p-8 text-center text-[#333333]">Caricamento…</div>}
      {!loading && items.length === 0 && <div className="p-8 text-center text-[#333333]" data-testid="admin-no-newsletter">Nessun iscritto alla newsletter al momento.</div>}
      {!loading && items.length > 0 && (
        <div className="divide-y divide-[#E5E7EB] max-h-72 overflow-y-auto">
          {items.map((s, i) => (
            <div key={s.id} className="px-6 py-3 flex items-center justify-between" data-testid={`newsletter-row-${i}`}>
              <p className="text-sm text-[#2d6fc4]">{s.email}</p>
              <p className="text-xs text-[#333333]">{fmtDT(s.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, sub, accent, big, icon: Icon }) => (
  <div className={`col-span-12 md:col-span-4 border border-[#E5E7EB] rounded-lg p-6 ${accent ? "bg-[#1a4a8a] text-[#FFFFFF] border-transparent" : "bg-[#FFFFFF]"}`}>
    <div className="flex items-center justify-between">
      <p className={`font-mono text-[10px] tracking-[0.25em] uppercase ${accent ? "text-white" : "text-[#2d6fc4]"}`}>— {label}</p>
      {Icon && <Icon size={16} strokeWidth={1.5} className={accent ? "text-white" : "text-[#2d6fc4]"}/>}
    </div>
    <p className={`font-serif mt-4 ${big ? "text-6xl" : "text-5xl"} leading-none`}>{value}</p>
    <p className={`mt-2 text-xs ${accent ? "text-[#FFFFFF]/70" : "text-[#333333]"}`}>{sub}</p>
  </div>
);

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
